import type { FC, ReactNode } from "react";

import type { ContentGateProps } from "@ui/components/ContentGate/ContentGate";
import { useViewport } from "@ui/hooks/useViewport";
import classNames from "classnames";

import { ChevronCircleForwardIcon } from "@jti/brand-icons/chevron-circle-forward";
import { Button } from "@jti/core-components/components/Button";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import { Loader } from "@jti/core-components/components/Loader";

import { Dialog } from "../Dialog";
import { LanguageSelector } from "../LanguageSelector";

import styles from "./ContentGatePopup.module.scss";

export interface ContentGatePopupProps extends ContentGateProps {
  externalContent?: ReactNode;
  isOpen: boolean;
  isAuthorMode?: boolean;
}

//TODO: change it to variable when style guide will be finished
const BREAKPOINT_SMALL_LARGE: number = 768;

export const ContentGatePopup: FC<ContentGatePopupProps> = ({
  externalContent,
  languages,
  logoSrc,
  title,
  whyContentRestricted,
  restrictions,
  isOpen,
  isAuthorMode,
}) => {
  const { width } = useViewport();

  const isMobileSmall = width < BREAKPOINT_SMALL_LARGE;

  const restrictionColumnClass = classNames(styles.restrictionColumn, "light-theme", {
    [styles.centered]: !restrictions.length,
  });

  return (
    <Dialog
      isOpened={isOpen}
      closable={false}
      noPadding={true}
      fullscreen={isMobileSmall}
      dataTestId="contentGatePopup"
      externalContent={externalContent}
      // TODO: remove after standardization of Shell Dialogs (https://jira.jtiweb.co.uk/browse/DESP-75945)
      backgroundBlur={70}
      backgroundColor="rgba(255, 255, 255, 0.5)"
      isUnwrapped={isAuthorMode}
      withContentPadding={false}
    >
      <div className={styles.container}>
        <div className={styles.mediaColumn}>
          {languages.length > 1 && (
            <div className={styles.languageSelectorWrapper}>
              <LanguageSelector
                customClassName={styles.contentRestrictionLanguageSelector}
                languages={languages}
                popupContext={"NAVIGATION"}
                variant={isMobileSmall ? "FULL_LANGUAGE_NAME" : "SHORT_LANGUAGE_NAME"}
              />
            </div>
          )}
          <div className={styles.content}>
            {logoSrc && (
              <div className={styles.logoWrapper}>
                <ImageBehaviour src={logoSrc} alt="logo" />
              </div>
            )}
            {title && <h2 className={styles.title}>{title}</h2>}
            {whyContentRestricted.label && (
              <Button variant="text" href={whyContentRestricted.link} className={styles.contentLink}>
                {whyContentRestricted.label}
              </Button>
            )}
          </div>
        </div>
        <div className={restrictionColumnClass}>
          {restrictions.length ? (
            restrictions.map(({ link, label }) => (
              <Button key={`${link}_${label}`} href={link} variant="text-muted" iconBefore={ChevronCircleForwardIcon}>
                {label}
              </Button>
            ))
          ) : (
            <Loader data-testid="loaderIcon" size="lg" />
          )}
        </div>
      </div>
    </Dialog>
  );
};
